import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect } from "react";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Undo2, Redo2, Quote, Minus,
} from "lucide-react";

/**
 * WYSIWYG editor. Emits HTML on every change.
 * value: current HTML string.
 * onChange(html): called with the new HTML.
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose max-w-none min-h-[220px] p-4 focus:outline-none prose-a:text-[color:var(--blue-deep)] prose-a:underline",
        "data-placeholder": placeholder,
      },
    },
  });

  // Sync when value prop changes externally (e.g. loading from API).
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove):", prev);
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const btn = (active, onClick, IconEl, label, testid) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testid}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors ${
        active
          ? "bg-[color:var(--blue-deep)] text-white border-[color:var(--blue-deep)]"
          : "bg-white text-[color:var(--ink-soft)] border-[color:var(--line)] hover:bg-[color:var(--cream)]"
      }`}
    >
      <IconEl className="w-4 h-4" />
    </button>
  );

  return (
    <div className="rounded-xl border border-[color:var(--line)] bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap gap-1 p-2 border-b border-[color:var(--line)] bg-[color:var(--cream)] rounded-t-xl">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold (⌘B)", "rte-bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic (⌘I)", "rte-italic")}
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Section heading", "rte-h2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, "Sub-heading", "rte-h3")}
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bulleted list", "rte-ul")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered list", "rte-ol")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quote, "Quote", "rte-quote")}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, "Divider", "rte-hr")}
        {btn(editor.isActive("link"), addLink, LinkIcon, "Insert / edit link", "rte-link")}
        <div className="w-px h-9 bg-[color:var(--line)] mx-1" />
        {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Undo", "rte-undo")}
        {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Redo", "rte-redo")}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
